/**
 * Normalizes accessibility quirks in Klaviyo onsite-form markup.
 *
 * Klaviyo's form renderer emits clickable images as
 * <img role="button" label="..."> nested inside a real <button>. The img's
 * role="button" creates a nested control with no aria name (the "label"
 * attribute is a leaked React prop, not real ARIA), which accessibility
 * scanners flag even though browsers fall back to the alt text.
 *
 * The theme cannot change the markup at its source (Klaviyo app embed), so
 * this listens for Klaviyo's documented `klaviyoForms` window events and
 * strips the redundant role, promoting the image's alt text to an explicit
 * aria-label on the owning button. No visual or behavioral change.
 */
(function () {
  'use strict';

  function accessibleNameFor(img) {
    // Prefer the visible text baked into the image (label-in-name), then
    // Klaviyo's leaked "label" prop. Never invent a name.
    return img.getAttribute('alt') || img.getAttribute('label') || '';
  }

  function normalize() {
    document
      .querySelectorAll('img[role="button"][class*="kl-private-reset-css"]')
      .forEach(function (img) {
        var button = img.closest('button');
        var name = accessibleNameFor(img);

        if (button) {
          // The real <button> already provides the control semantics; the
          // img's role only nests a nameless second control inside it.
          img.removeAttribute('role');
          if (name && !button.hasAttribute('aria-label') && button.textContent.trim() === '') {
            button.setAttribute('aria-label', name);
          }
        } else if (name && !img.hasAttribute('aria-label')) {
          // Standalone clickable image: keep the role, give it the name.
          img.setAttribute('aria-label', name);
        }
      });
  }

  // Klaviyo fires this for open / embedOpen / stepSubmit / close etc.
  // The form (re-)renders around the event, so defer one macrotask.
  // setTimeout, not requestAnimationFrame: rAF never fires on hidden tabs.
  window.addEventListener('klaviyoForms', function () {
    setTimeout(normalize, 0);
  });

  // Sweep anything already rendered before this script ran.
  normalize();
})();
